/**
 * TvButton — Triveny primary / ink / ghost button.
 *
 *   <TvButton variant="primary" icon={Phone} onClick={start}>Start call</TvButton>
 *   <TvButton variant="ink" icon={Zap}>Quick actions</TvButton>
 *   <TvButton variant="ghost" icon={LayoutGrid}>Modules</TvButton>
 *
 * Variants:
 *   primary → cyan accent fill, white text (CTA, "Start call")
 *   ink     → ink fill, white text (secondary primary, "Quick actions")
 *   ghost   → surface bg, ink text, hairline border (tertiary, "Modules")
 *
 * Spec: 32px tall · radius var(--v2-r) · gap 7 · font-size 12.5 · weight 500.
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { tvTokens } from './tokens';

type TvButtonVariant = 'primary' | 'ink' | 'ghost';

interface TvButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: TvButtonVariant;
  icon?: LucideIcon;
  /** Optional override for the inline style block. */
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const PALETTE: Record<
  TvButtonVariant,
  { bg: string; color: string; border: string }
> = {
  primary: { bg: tvTokens.accent, color: '#fff', border: 'transparent' },
  ink: { bg: tvTokens.ink, color: '#fff', border: 'transparent' },
  ghost: { bg: tvTokens.surface, color: tvTokens.ink, border: tvTokens.border },
};

export function TvButton({
  variant = 'primary',
  icon: Icon,
  children,
  style,
  type = 'button',
  ...rest
}: TvButtonProps) {
  const p = PALETTE[variant];
  return (
    <button
      type={type}
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        height: 32,
        padding: '0 13px',
        borderRadius: tvTokens.r,
        fontSize: 12.5,
        fontWeight: 500,
        cursor: 'pointer',
        border: `1px solid ${p.border}`,
        background: p.bg,
        color: p.color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
}
